/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone, OnChanges} from '@angular/core';
import * as _ from 'lodash';
import {AngularModalService} from '../../common/modal/angular-modal.service';
import template from './product-edit-page.component.html';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {ADN_TARGETING_TYPES} from '../../targeting/targeting-constants';
import {LayoutSelectorModalComponent} from './layout-selector-modal.component';
import {TargetingEditorModalService} from '../../targeting/targeting-editor-modal.service';
import {OrderResource} from '../../../components/api/resources/resource/order.resource';
import {LayoutResource} from '../../../components/api/resources/resource/layout.resource';
import {ReportTemplateResource} from '../../../components/api/resources/resource/report-template.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';


@Component({
  standalone: false,
  selector: 'adn-product-edit-page',
  template
})
export class ProductEditPageComponent extends AdminResourceEditPageBase implements OnChanges {
  hasLayouts = false;
  isSelfService = false;
  suiOrders: any[] | null = null;
  reportTemplates: any[] | null = null;
  tiers: any[] = [];
  targetingResult: any = {};
  creativeTargetingResult: any = {};
  layoutResult: any = {};

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LayoutResource) private Layout: any,
    @Inject(OrderResource) private Order: any,
    @Inject(ReportTemplateResource) private ReportTemplate: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    ngZone: NgZone,
    private angularModalService: AngularModalService,
    private targetingEditorModalService: TargetingEditorModalService,
    private cdr: ChangeDetectorRef
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.isSelfService = LocalUserPermissions.isSelfService();

    Order.query({joins: 'inner join team on team.id = order.teamId', where: 'team.type in SUI'}).$promise.then((page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.suiOrders = page.results;
      });
    });

    ReportTemplate.query({filterBy: 'reportType', filterByValue: 'SELF_SERVICE'}).$promise.then((page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.reportTemplates = page.results;
      });
    });
  }

  protected afterInitEditingModel(): void {
    this.hasLayouts = false;
    if (this.editingModel.layouts && this.editingModel.layouts.length) {
      this.Layout.query(_.map(this.editingModel.layouts, 'id'), (page: any) => {
        // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
        deferMarkForCheck(this.cdr, () => {
          this.editingModel.layouts = page.results;
          this.hasLayouts = true;
        });
      });
    } else {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.hasLayouts = true;
      });
    }

    this.editingModel.layouts = this.editingModel.layouts || [];
    this.editingModel.cpmSpecification = this.editingModel.cpmSpecification || 'FIXED';

    this.editingModel.allowedDimensions = _.isArray(this.editingModel.allowedDimensions) ? this.editingModel.allowedDimensions : [[]];
    this.editingModel.allowedDimensions[0] = _.isArray(this.editingModel.allowedDimensions[0]) ? this.editingModel.allowedDimensions[0] : [];

    this.editingModel.data = this.editingModel.data || {};
    this.editingModel.data.presentationOptions = this.editingModel.data.presentationOptions || {};

    this.editingModel.targeting = this.editingModel.targeting || {};
    this.editingModel.creativeTargeting = this.editingModel.creativeTargeting || {};

    this.targetingResult = {};
    this.creativeTargetingResult = {};
    this.layoutResult = {};

    const enabledTiers = this.LocalNetworkProfile.get('tiers') || [];
    const currentTier = this.editingModel.defaultTier;
    this.tiers = _.cloneDeep(enabledTiers);
    if (currentTier) {
      const hasCurrent = !!_.find(this.tiers, (t: any) => t.id === _.get(currentTier, 'id'));
      if (!hasCurrent) {
        this.tiers.push(currentTier);
      }
    }
    _.forEach(this.tiers, (t: any) => {
      t.displayName = t.name + (_.find(enabledTiers, {id: t.id}) ? '' : ' (disabled)');
    });

    this.editingModel.defaultOrders = _.map(this.editingModel.defaultOrders, (o: any) => _.isString(o) ? o : o.id);
  }

  protected afterInitAfterSave(): void {
    this.targetingResult.data = false;
    this.creativeTargetingResult.data = false;
  }

  protected onBeforeSubmit(): void {
    if (this.editingModel.minimumCpm && !_.isFinite(this.editingModel.minimumCpm.amount)) {
      this.editingModel.minimumCpm = null;
    }
    if (this.editingModel.minimumCpc && !_.isFinite(this.editingModel.minimumCpc.amount)) {
      this.editingModel.minimumCpc = null;
    }
  }

  addDimensions(): void {
    this.editingModel.allowedDimensions = this.editingModel.allowedDimensions || [];
    this.editingModel.allowedDimensions.push([]);
    this.markExternalFormDirty();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.allowedDimensions, index);
    this.markExternalFormDirty();
  }

  isInDefaultOrders(orderId: string): boolean {
    return (this.editingModel.defaultOrders || []).indexOf(orderId) > -1;
  }

  toggleDefaultOrder(orderId: string): void {
    this.editingModel.defaultOrders = this.editingModel.defaultOrders || [];
    const idx = this.editingModel.defaultOrders.indexOf(orderId);
    if (idx > -1) {
      this.editingModel.defaultOrders.splice(idx, 1);
    } else {
      this.editingModel.defaultOrders.push(orderId);
    }
    this.markExternalFormDirty();
  }

  editTarget(targeting: any, isCreative = false): void {
    const typeTargeting = isCreative ? 'creativeTargetingResult' : 'targetingResult';
    const apiParam = isCreative ? 'creativeTargeting' : 'targeting';
    this[typeTargeting] = {data: false};

    this.targetingEditorModalService.open({
      modelLineItem: _.cloneDeep(this.editingModel),
      lineItemType: 'NORMAL',
      pageType: isCreative ? 'optionalProduct' : 'product',
      reachEnabled: undefined,
      mandatoryTargets: undefined,
      availableTargets: isCreative ? _.pick(ADN_TARGETING_TYPES, ['site', 'siteGroup', 'userSegment', 'category', 'namedLocation', 'device', 'keyValue', 'domain']) : null,
      targeting,
      overridableTargeting: undefined,
      targetingMode: null,
      isLockedDown: false
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(targeting, wc.wc);
      this.editingModel[apiParam] = _.cloneDeep(targeting);
      this.model[apiParam] = _.cloneDeep(targeting);
      if (!this.isNew) {
        this.model.savePartial(apiParam).then((respObj: any) => {
          this.ngZone.run(() => {
            this[typeTargeting].data = true;
            _.set(this.editingModel, 'validationWarnings', respObj.validationWarnings);
          });
        });
      } else {
        this.markExternalFormDirty();
      }
    });
  }

  editLayouts(): void {
    this.ngZone.run(() => {
      this.layoutResult = {data: false};
      const modalRef = this.angularModalService.show(LayoutSelectorModalComponent, {
        class: 'modal-lg',
        initialState: {currentLayouts: _.cloneDeep(this.editingModel.layouts)}
      });
      (modalRef.content as LayoutSelectorModalComponent).onSaved.subscribe((layouts: any[]) => {
        this.ngZone.run(() => {
          this.editingModel.layouts = layouts;
          this.markExternalFormDirty();
          if (!this.isNew) {
            this.model.savePartial('layouts').then((respObj: any) => {
              this.ngZone.run(() => {
                this.layoutResult.data = true;
                _.set(this.editingModel, 'validationWarnings', respObj.validationWarnings);
              });
            });
          }
        });
      });
    });
  }
}
